import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DashboardIcon from '@mui/icons-material/Dashboard';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';

type WidgetKind = 'template' | 'custom' | 'kpi';
type WidgetWidth = 'third' | 'half' | 'full';

interface IWidget {
  idDashboardWidget: number;
  Kind: WidgetKind;
  Title?: string | null;
  RefId: string;
  RefConfig?: Record<string, unknown>;
  Position: number;
  Width: WidgetWidth;
}

interface IReportItem {
  label: string;
  value: number;
}

interface IReportConfig {
  id: string;
  title: string;
  defaultChart: 'bar' | 'pie' | 'line';
}

interface ICustomReportEntry {
  idCustomReport: number;
  Title: string;
  ChartType: 'bar' | 'pie' | 'line';
}

interface IKpiAlert {
  idKpiAlert: number;
  Title: string;
  Operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq';
  Threshold: number;
  LastValue?: number | null;
  LastEvaluatedAt?: string | null;
  IsActive: boolean;
}

const PIE_COLORS = [
  '#1976d2',
  '#388e3c',
  '#f57c00',
  '#c2185b',
  '#7b1fa2',
  '#0097a7',
  '#5d4037',
  '#455a64',
];

const WIDTH_TO_GRID: Record<WidgetWidth, number> = {
  third: 4,
  half: 6,
  full: 12,
};

/**
 * Persönliches Notar-Dashboard. Tile-Liste, jede Kachel rendert wahlweise
 * einen Vorlagen-Report, einen eigenen CustomReport oder ein KPI-Tile mit
 * aktuellem Wert + Sparkline.
 */
export const NotaryDashboard: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: widgets, isLoading: isLoadingWidgets } = useQuery<IWidget[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/dashboard/me'],
    queryFn: () => customFetcher({ endpoint: '/dashboard/me' }),
  });

  const { data: reportTemplates } = useQuery<{ items: IReportConfig[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['/reports'],
    queryFn: () => customFetcher({ endpoint: '/reports' }),
  });

  const { data: customReports } = useQuery<ICustomReportEntry[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/customreport'],
    queryFn: () => customFetcher({ endpoint: '/customreport' }),
  });

  const { data: kpis } = useQuery<IKpiAlert[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/kpialert'],
    queryFn: () => customFetcher({ endpoint: '/kpialert' }),
  });

  const invalidateWidgets = () =>
    queryClient.invalidateQueries({ queryKey: ['/dashboard/me'] });

  const { mutateAsync: createWidget } = useMutation({
    mutationFn: (input: Partial<IWidget>) =>
      customFetcher({
        endpoint: '/dashboard/me',
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidateWidgets,
  });
  const { mutateAsync: updateWidget } = useMutation({
    mutationFn: (input: { id: number; data: Partial<IWidget> }) =>
      customFetcher({
        endpoint: `/dashboard/me/${input.id}`,
        method: 'PUT',
        body: input.data,
      }),
    onSuccess: invalidateWidgets,
  });
  const { mutateAsync: deleteWidget } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({
        endpoint: `/dashboard/me/${id}`,
        method: 'DELETE',
      }),
    onSuccess: invalidateWidgets,
  });

  const [addOpen, setAddOpen] = useState(false);
  const [draftKind, setDraftKind] = useState<WidgetKind>('template');
  const [draftRefId, setDraftRefId] = useState<string>('');
  const [draftTitle, setDraftTitle] = useState('');
  const [draftWidth, setDraftWidth] = useState<WidgetWidth>('half');

  const sortedWidgets = useMemo(
    () => (widgets || []).slice().sort((a, b) => a.Position - b.Position),
    [widgets]
  );

  const handleAdd = async () => {
    if (!draftRefId) return;
    await createWidget({
      Kind: draftKind,
      RefId: String(draftRefId),
      Title: draftTitle.trim() || null,
      Width: draftWidth,
    });
    setAddOpen(false);
    setDraftRefId('');
    setDraftTitle('');
  };

  const handleMove = async (w: IWidget, dir: 'up' | 'down') => {
    const list = sortedWidgets;
    const idx = list.findIndex(
      x => x.idDashboardWidget === w.idDashboardWidget
    );
    if (idx < 0) return;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= list.length) return;
    const other = list[swapWith];
    await Promise.all([
      updateWidget({
        id: w.idDashboardWidget,
        data: { Position: other.Position },
      }),
      updateWidget({
        id: other.idDashboardWidget,
        data: { Position: w.Position },
      }),
    ]);
  };

  const handleResize = (w: IWidget) => {
    const next: WidgetWidth =
      w.Width === 'third' ? 'half' : w.Width === 'half' ? 'full' : 'third';
    void updateWidget({ id: w.idDashboardWidget, data: { Width: next } });
  };

  const handleRemove = async (w: IWidget) => {
    if (
      !window.confirm(`Kachel „${w.Title || w.RefId}" vom Dashboard entfernen?`)
    )
      return;
    await deleteWidget(w.idDashboardWidget);
  };

  const refreshAll = () => {
    invalidateWidgets();
    queryClient.invalidateQueries({ queryKey: ['/widget-data'] });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <DashboardIcon color='primary' fontSize='large' />
          <Box>
            <Typography variant='h5'>Mein Dashboard</Typography>
            <Typography variant='caption' color='text.secondary'>
              Persönlich zusammengestellte Reports und KPIs auf einen Blick.
            </Typography>
          </Box>
        </Stack>
        <Stack direction='row' spacing={1}>
          <Button
            variant='outlined'
            startIcon={<RefreshIcon />}
            onClick={refreshAll}
          >
            Aktualisieren
          </Button>
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Kachel hinzufügen
          </Button>
        </Stack>
      </Stack>

      {isLoadingWidgets ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !sortedWidgets.length ? (
        <Alert severity='info'>
          Ihr Dashboard ist noch leer. Klicken Sie auf „Kachel hinzufügen", um
          einen Report oder KPI als Tile aufzunehmen.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sortedWidgets.map((w, idx) => (
            <Grid
              key={w.idDashboardWidget}
              item
              xs={12}
              md={WIDTH_TO_GRID[w.Width]}
            >
              <WidgetTile
                widget={w}
                onMoveUp={idx > 0 ? () => void handleMove(w, 'up') : undefined}
                onMoveDown={
                  idx < sortedWidgets.length - 1
                    ? () => void handleMove(w, 'down')
                    : undefined
                }
                onResize={() => handleResize(w)}
                onRemove={() => void handleRemove(w)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Kachel hinzufügen</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label='Typ'
              select
              size='small'
              fullWidth
              value={draftKind}
              onChange={e => {
                setDraftKind(e.target.value as WidgetKind);
                setDraftRefId('');
              }}
            >
              <MenuItem value='template'>Vorlagen-Report</MenuItem>
              <MenuItem value='custom'>Eigener Report</MenuItem>
              <MenuItem value='kpi'>KPI / Alert</MenuItem>
            </TextField>

            {draftKind === 'template' && (
              <Autocomplete
                size='small'
                options={reportTemplates?.items || []}
                getOptionLabel={r => r.title}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={
                  reportTemplates?.items.find(r => r.id === draftRefId) ?? null
                }
                onChange={(_, v) => setDraftRefId(v ? v.id : '')}
                renderInput={p => <TextField {...p} label='Report wählen' />}
              />
            )}
            {draftKind === 'custom' && (
              <Autocomplete
                size='small'
                options={customReports || []}
                getOptionLabel={r => r.Title}
                isOptionEqualToValue={(o, v) =>
                  o.idCustomReport === v.idCustomReport
                }
                value={
                  customReports?.find(
                    r => String(r.idCustomReport) === draftRefId
                  ) ?? null
                }
                onChange={(_, v) =>
                  setDraftRefId(v ? String(v.idCustomReport) : '')
                }
                renderInput={p => (
                  <TextField {...p} label='Eigenen Report wählen' />
                )}
              />
            )}
            {draftKind === 'kpi' && (
              <Autocomplete
                size='small'
                options={kpis || []}
                getOptionLabel={k => k.Title}
                isOptionEqualToValue={(o, v) => o.idKpiAlert === v.idKpiAlert}
                value={
                  kpis?.find(k => String(k.idKpiAlert) === draftRefId) ?? null
                }
                onChange={(_, v) =>
                  setDraftRefId(v ? String(v.idKpiAlert) : '')
                }
                renderInput={p => <TextField {...p} label='KPI wählen' />}
              />
            )}

            <TextField
              label='Titel (optional)'
              size='small'
              fullWidth
              value={draftTitle}
              onChange={e => setDraftTitle(e.target.value)}
              placeholder='Lassen Sie das Feld leer, um den Original-Titel zu nutzen.'
            />

            <TextField
              label='Breite'
              select
              size='small'
              fullWidth
              value={draftWidth}
              onChange={e => setDraftWidth(e.target.value as WidgetWidth)}
            >
              <MenuItem value='third'>Drittel-Breite</MenuItem>
              <MenuItem value='half'>Halbe Breite</MenuItem>
              <MenuItem value='full'>Volle Breite</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={!draftRefId}
            onClick={handleAdd}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================================
//  Tile-Renderer für die drei Widget-Typen
// ============================================================================
interface ITileProps {
  widget: IWidget;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onResize: () => void;
  onRemove: () => void;
}

const WidgetTile: React.FC<ITileProps> = ({
  widget,
  onMoveUp,
  onMoveDown,
  onResize,
  onRemove,
}) => {
  const header = (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={{ mb: 1 }}
    >
      <Typography variant='subtitle1' fontWeight={600}>
        {widget.Title || <WidgetTitle widget={widget} />}
      </Typography>
      <Stack direction='row' spacing={0}>
        <Tooltip title='Nach oben'>
          <span>
            <IconButton size='small' onClick={onMoveUp} disabled={!onMoveUp}>
              <ArrowUpwardIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Nach unten'>
          <span>
            <IconButton
              size='small'
              onClick={onMoveDown}
              disabled={!onMoveDown}
            >
              <ArrowDownwardIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title='Breite umschalten (Drittel → Halb → Voll → Drittel)'>
          <IconButton size='small' onClick={onResize}>
            <DashboardIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Vom Dashboard entfernen'>
          <IconButton size='small' onClick={onRemove}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );

  return (
    <Card variant='outlined' sx={{ height: '100%' }}>
      <CardContent>
        {header}
        {widget.Kind === 'template' && (
          <TemplateTileBody refId={widget.RefId} />
        )}
        {widget.Kind === 'custom' && (
          <CustomTileBody idCustomReport={Number(widget.RefId)} />
        )}
        {widget.Kind === 'kpi' && (
          <KpiTileBody idKpiAlert={Number(widget.RefId)} />
        )}
      </CardContent>
    </Card>
  );
};

// Klein helper, um Default-Titel anzuzeigen, wenn kein eigener gesetzt ist.
const WidgetTitle: React.FC<{ widget: IWidget }> = ({ widget }) => {
  if (widget.Kind === 'template') {
    const { data } = useQuery<{ items: IReportConfig[] }>({
      refetchOnWindowFocus: false,
      queryKey: ['/reports'],
      queryFn: () => customFetcher({ endpoint: '/reports' }),
      enabled: false,
    });
    const t = data?.items.find(i => i.id === widget.RefId);
    return <>{t?.title || widget.RefId}</>;
  }
  if (widget.Kind === 'custom') {
    return <>Eigener Report #{widget.RefId}</>;
  }
  return <>KPI #{widget.RefId}</>;
};

// ============================================================================
//  Body-Renderer (Daten + Chart)
// ============================================================================
const renderChart = (
  items: IReportItem[],
  ct: 'bar' | 'pie' | 'line',
  height = 240
) => {
  if (!items?.length) {
    return (
      <Alert severity='info' sx={{ mt: 1 }}>
        Keine Daten.
      </Alert>
    );
  }
  if (ct === 'pie') {
    return (
      <ResponsiveContainer width='100%' height={height}>
        <PieChart>
          <Pie
            data={items}
            dataKey='value'
            nameKey='label'
            cx='50%'
            cy='50%'
            outerRadius={Math.min(110, height / 2 - 20)}
            label
          >
            {items.map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Legend />
          <RechartsTooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (ct === 'line') {
    return (
      <ResponsiveContainer width='100%' height={height}>
        <LineChart data={items}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis dataKey='label' />
          <YAxis />
          <RechartsTooltip />
          <Line
            type='monotone'
            dataKey='value'
            stroke='#1976d2'
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width='100%' height={height}>
      <BarChart data={items}>
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis dataKey='label' />
        <YAxis />
        <RechartsTooltip />
        <Bar dataKey='value' fill='#1976d2' />
      </BarChart>
    </ResponsiveContainer>
  );
};

const TemplateTileBody: React.FC<{ refId: string }> = ({ refId }) => {
  const { data: configs } = useQuery<{ items: IReportConfig[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['/reports'],
    queryFn: () => customFetcher({ endpoint: '/reports' }),
  });
  const cfg = configs?.items.find(c => c.id === refId);

  const { data, isLoading, error } = useQuery<{
    items: IReportItem[];
    totals?: { count?: number; sum?: number };
  }>({
    refetchOnWindowFocus: false,
    queryKey: ['/widget-data', 'template', refId],
    queryFn: () => customFetcher({ endpoint: `/reports/${refId}` }),
  });

  if (isLoading) return <CircularProgress size={24} />;
  if (error)
    return (
      <Alert severity='error'>
        {error instanceof Error ? error.message : 'Fehler beim Laden.'}
      </Alert>
    );
  return (
    <>
      {renderChart(data?.items || [], cfg?.defaultChart || 'bar')}
      {data?.totals && (
        <Stack direction='row' spacing={2} sx={{ mt: 1 }}>
          {data.totals.count != null && (
            <Chip size='small' label={`Gesamt-Anzahl: ${data.totals.count}`} />
          )}
          {data.totals.sum != null && (
            <Chip
              size='small'
              label={`Volumen: ${new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
              }).format(data.totals.sum)}`}
            />
          )}
        </Stack>
      )}
    </>
  );
};

const CustomTileBody: React.FC<{ idCustomReport: number }> = ({
  idCustomReport,
}) => {
  const { data: meta } = useQuery<ICustomReportEntry[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/customreport'],
    queryFn: () => customFetcher({ endpoint: '/customreport' }),
  });
  const entry = meta?.find(m => m.idCustomReport === idCustomReport);
  const { data, isLoading } = useQuery<{ items: IReportItem[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['/widget-data', 'custom', idCustomReport],
    queryFn: () =>
      customFetcher({
        endpoint: `/customreport/${idCustomReport}/execute`,
      }),
  });
  if (isLoading) return <CircularProgress size={24} />;
  return renderChart(data?.items || [], entry?.ChartType || 'bar');
};

const KpiTileBody: React.FC<{ idKpiAlert: number }> = ({ idKpiAlert }) => {
  const { data: kpis } = useQuery<IKpiAlert[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/kpialert'],
    queryFn: () => customFetcher({ endpoint: '/kpialert' }),
  });
  const kpi = kpis?.find(k => k.idKpiAlert === idKpiAlert);

  const { data: history } = useQuery<{
    items: Array<{
      value: number | null;
      threshold: number | null;
      at: string;
    }>;
  }>({
    refetchOnWindowFocus: false,
    queryKey: ['/widget-data', 'kpi', idKpiAlert],
    queryFn: () =>
      customFetcher({
        endpoint: `/kpialert/${idKpiAlert}/history?limit=30`,
      }),
  });

  if (!kpi) {
    return <Alert severity='warning'>KPI nicht gefunden.</Alert>;
  }

  const lastValue = kpi.LastValue ?? null;
  const triggered = (() => {
    if (lastValue == null) return false;
    const t = kpi.Threshold;
    switch (kpi.Operator) {
      case 'gt':
        return lastValue > t;
      case 'gte':
        return lastValue >= t;
      case 'lt':
        return lastValue < t;
      case 'lte':
        return lastValue <= t;
      case 'eq':
        return lastValue === t;
    }
  })();

  return (
    <Stack spacing={1}>
      <Stack direction='row' alignItems='baseline' spacing={1}>
        <Typography variant='h3' fontWeight={700}>
          {lastValue != null ? lastValue : '–'}
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          (Schwelle: {kpi.Threshold})
        </Typography>
      </Stack>
      <Chip
        size='small'
        label={triggered ? 'Bedingung erfüllt' : 'Bedingung nicht erfüllt'}
        color={triggered ? 'warning' : 'default'}
        variant='outlined'
      />
      <Box sx={{ height: 110 }}>
        <ResponsiveContainer width='100%' height='100%'>
          <LineChart
            data={history?.items || []}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <ReferenceLine
              y={kpi.Threshold}
              stroke='#f57c00'
              strokeDasharray='3 3'
            />
            <Line
              type='monotone'
              dataKey='value'
              stroke='#1976d2'
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
            <RechartsTooltip
              formatter={(v: number) => [v, 'Wert']}
              labelFormatter={(_, payload) => {
                const at = payload?.[0]?.payload?.at;
                return at ? new Date(at).toLocaleString('de-DE') : '';
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Stack>
  );
};
